import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Popover } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import { additionMenuItems, mainMenuItems } from '../../constants/nav';
import { routes } from '../../constants/routing';
import useWindowSize from '../../hooks/useWindowSize';
import { toggleUiElement } from '../../store/actions/shell';
import { selectAdminType, selectIsAuthenticated } from '../../store/selectors/auth';

import { selectIsAutomationsEnabled } from '../../store/selectors/organizations';
import { selectIsSidenavShown } from '../../store/selectors/shell';
import { TSidenavItem } from '../../types/sidenav';
import { hasPermission } from '../../utils/users';
import { DesktopSidenav, MobileSidenav, NavAccordionItem, NavIcon, NavItem, NavText, NonAuthSidenav } from './index';

import styles from './Sidenav.module.scss';

const Sidenav = () => {
  const { shadows, sass: variables } = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedItem, setSelectedItem] = useState('');
  const [wasExpanded, setWasExpanded] = useState(false);
  const [popoverState, setPopoverState] = useState<{ anchorEl: HTMLElement; item: TSidenavItem } | null>(null);
  const { width, mobile, fullHD } = useWindowSize();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoginRoute = location.pathname.startsWith(routes.login);
  const adminType = useSelector(selectAdminType);
  const sidenavIsShown = useSelector(selectIsSidenavShown);
  const isAutomationsEnabled: boolean = useSelector(selectIsAutomationsEnabled);

  const setExpandedSideNav = useCallback(
    (value?: boolean) =>
      dispatch(toggleUiElement({ sidenavIsShown: typeof value !== 'undefined' ? value : !sidenavIsShown })),
    [dispatch, sidenavIsShown],
  );

  // Set initial values of the selectedItem
  useEffect(() => {
    if (isAuthenticated) {
      const getInitialSelectedItem = (menuItems: TSidenavItem[]): TSidenavItem | undefined => {
        return menuItems.find((item: TSidenavItem) => {
          if (item.subItems) {
            return getInitialSelectedItem(item.subItems);
          }
          return item.route ? location.pathname.includes(item.route) : undefined;
        });
      };

      const initialItem = getInitialSelectedItem([...mainMenuItems, ...additionMenuItems]);
      if (initialItem) {
        setSelectedItem(initialItem.title);
      }
    } else {
      setSelectedItem('');
    }
  }, [isAuthenticated, location]);

  useEffect(() => {
    if (!width) {
      return;
    }

    // If it's undefined - so it was never set or persisted, therefore this is a new user.
    // If such user has a fullHD screen - we should show sidenav opened initially.
    // Otherwise - show it closed
    if (typeof sidenavIsShown === 'undefined') {
      setExpandedSideNav(fullHD);
      return;
    }
  }, [sidenavIsShown, width, setExpandedSideNav, fullHD]);

  const toggle = useCallback(() => {
    setExpandedSideNav(!sidenavIsShown);
  }, [sidenavIsShown, setExpandedSideNav]);

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLElement>, item: TSidenavItem) => {
    setPopoverState({ anchorEl: event.currentTarget, item });
  }, []);

  const handleClosePopover = useCallback(() => {
    setPopoverState(null);
  }, []);

  const filterByDeviceType = useCallback(
    (item: TSidenavItem) => (mobile && item.usedInMobile) || (!mobile && item.usedInDesktop),
    [mobile],
  );

  const filterByFeatureFlags = useCallback(
    (item: TSidenavItem) => {
      if (item.route === routes.automations) {
        return isAutomationsEnabled;
      }
      return true;
    },
    [isAutomationsEnabled],
  );

  const filterByRole = useCallback((item: TSidenavItem) => hasPermission(item.roles, adminType), [adminType]);

  const handleExpandedLogicWithSubItems = useCallback(
    ({ subItems, isSubItem }: TSidenavItem) => {
      /* if the user wants to choose Campaigns Editor from the minimized bar,
       * he should click on the ‘campaigns manager’ icon and the bar will become wider with text descriptions.
       * Once the user picked the sub page, the bar will become minimized again.
       */
      if (subItems && !sidenavIsShown) {
        toggle();
        setWasExpanded(true);
      } else if (wasExpanded && isSubItem) {
        toggle();
        setWasExpanded(false);
      } else {
        setWasExpanded(false);
      }
    },
    [toggle, sidenavIsShown, wasExpanded],
  );

  const handleMenuItemClick = useCallback(
    (menuItem: TSidenavItem) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      const { title, subItems, isSubItem } = menuItem;

      if (isSubItem) {
        handleClosePopover();
      }

      if (mobile && !subItems) {
        toggle();
      }

      // Disabling NavLink redirect for nav item with the sub items
      if (subItems) {
        event.preventDefault();

        if (!mobile) {
          // Desktop: always show popover
          handleOpenPopover(event as unknown as React.MouseEvent<HTMLElement>, menuItem);
        } else {
          // Mobile: expand sidenav and show accordion
          handleExpandedLogicWithSubItems(menuItem);
        }
      }

      setSelectedItem(title === selectedItem ? '' : title);
    },
    [
      mobile,
      toggle,
      handleExpandedLogicWithSubItems,
      selectedItem,
      sidenavIsShown,
      handleOpenPopover,
      handleClosePopover,
    ],
  );

  // filter out the Automations if this option is set to false in org
  const filterMenuItems = useCallback(
    (
      itemsArray: TSidenavItem[],
      deviceFilter: (item: TSidenavItem) => boolean | undefined,
      roleFilter: (item: TSidenavItem) => boolean,
      flagFilter: (item: TSidenavItem) => boolean,
    ) => {
      return itemsArray.filter(deviceFilter).filter(roleFilter).filter(flagFilter);
    },
    [],
  );

  const getMenuItems = useCallback(
    (items: TSidenavItem[], inPopover: boolean = false) => {
      const filteredItems = filterMenuItems(items, filterByDeviceType, filterByRole, filterByFeatureFlags);

      return filteredItems.map((menuItem: TSidenavItem) => {
        const { dataAction, icon: IconComponent, iconActive: IconActiveComponent, title, route, subItems } = menuItem;

        const isExpanded = !mobile
          ? title === popoverState?.item.title
          : title === selectedItem || subItems?.some((item: TSidenavItem) => item.title === selectedItem);

        const isItemActive =
          (route && location.pathname.includes(route)) ||
          (subItems?.some((el: TSidenavItem) => el.route && location.pathname.includes(el.route)) ?? false) ||
          isExpanded;

        const isSubItemActive = inPopover && route && location.pathname.includes(route);

        const CurrentIcon =
          (isItemActive || isSubItemActive) && IconActiveComponent ? IconActiveComponent : IconComponent;

        if (mobile && !!subItems) {
          return (
            <NavAccordionItem
              key={title}
              title={title}
              icon={IconComponent}
              activeIcon={IconActiveComponent}
              isActive={isItemActive || false}
              isExpanded={isExpanded || false}
              onChange={() => setSelectedItem(isExpanded ? '' : title)}
            >
              <div className={styles.subItems}>{getMenuItems(subItems)}</div>
            </NavAccordionItem>
          );
        }

        return (
          <React.Fragment key={title}>
            <NavItem>
              <NavLink
                isActive={(match, currentLocation) => {
                  if (inPopover) {
                    return !!match;
                  }

                  if (!match) {
                    return false;
                  }

                  // If there is no route, so this element has subItems
                  // Сompare location path with children routes for  highlighting the parent element
                  return !!(
                    route || subItems?.some((el: TSidenavItem) => currentLocation.pathname?.includes(el.route || ''))
                  );
                }}
                className={cn(styles.menuItem, {
                  [styles.expanded]: isExpanded && !!subItems,
                })}
                activeClassName={styles.activated}
                to={route || ''}
                onClick={handleMenuItemClick(menuItem)}
                data-action={dataAction}
              >
                {IconComponent && (
                  <NavIcon className={cn(styles.icon, { [styles.smallIcon]: inPopover })}>
                    <CurrentIcon />
                  </NavIcon>
                )}
                {sidenavIsShown || mobile || inPopover ? (
                  <>
                    <NavText title={title} inPopover={inPopover} />
                    {!!subItems && !inPopover && !mobile && (
                      <ChevronRightIcon className={cn(styles.expandIcon, { [styles.expanded]: isExpanded })} />
                    )}
                  </>
                ) : null}
              </NavLink>
            </NavItem>
          </React.Fragment>
        );
      });
    },
    [
      filterByDeviceType,
      filterByRole,
      handleMenuItemClick,
      sidenavIsShown,
      mobile,
      selectedItem,
      popoverState,
      filterMenuItems,
      filterByFeatureFlags,
      location.pathname,
    ],
  );
  const mainMenu = useMemo(() => getMenuItems(mainMenuItems), [getMenuItems]);
  const additionalMenu = useMemo(() => getMenuItems(additionMenuItems), [getMenuItems]);

  const nonAuth = useMemo(() => {
    if (isLoginRoute) {
      return <NonAuthSidenav />;
    }
    return mobile ? (
      <MobileSidenav
        expanded={Boolean(sidenavIsShown)}
        toggle={toggle}
        mainMenu={mainMenu}
        additionalMenu={additionalMenu}
        isAuth={isAuthenticated}
      />
    ) : (
      <NonAuthSidenav />
    );
  }, [mobile, isLoginRoute, sidenavIsShown, mainMenu, additionalMenu, isAuthenticated, toggle]);

  const auth = useMemo(() => {
    return mobile ? (
      <MobileSidenav
        expanded={Boolean(sidenavIsShown)}
        toggle={toggle}
        mainMenu={mainMenu}
        additionalMenu={additionalMenu}
        isAuth={isAuthenticated}
      />
    ) : (
      <DesktopSidenav expanded={Boolean(sidenavIsShown)} toggle={toggle} mainMenu={mainMenu} />
    );
  }, [mobile, sidenavIsShown, toggle, mainMenu, additionalMenu, isAuthenticated]);

  const popover = useMemo(() => {
    if (!popoverState?.item.subItems) {
      return null;
    }

    return (
      <Popover
        open={Boolean(popoverState)}
        anchorEl={popoverState.anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              marginLeft: `calc(${sidenavIsShown ? variables.navDesktopMaxWidth : variables.navDesktopMinWidth} - 8px)`,
              boxShadow: shadows[8],
              borderRadius: 1,
            },
          },
        }}
      >
        <div className={styles.subItemsPopover}>{getMenuItems(popoverState.item.subItems, true)}</div>
      </Popover>
    );
  }, [popoverState, handleClosePopover, sidenavIsShown, shadows, getMenuItems, variables]);

  return (
    <aside className={cn(styles.sideNav, !isAuthenticated && (isLoginRoute || !mobile) ? styles.nonAuth : null)}>
      {isAuthenticated ? auth : nonAuth}
      {popover}
    </aside>
  );
};

export default Sidenav;
